import { Head } from 'vite-react-ssg'
import type { SeoFrontmatter } from '../types/page'

// `Head` is vite-react-ssg's re-export of react-helmet-async's Helmet. Using
// it is what bakes these tags into the pre-rendered HTML — crawlers and link
// unfurlers that don't run JS still see them.

export const SITE = 'https://help.chosenhq.com'
const DEFAULT_OG = `${SITE}/og-default.png`

interface Props {
  frontmatter: SeoFrontmatter
  path: string
  ogType?: 'website' | 'article'
}

export default function SeoHead({ frontmatter, path, ogType = 'article' }: Props) {
  const fullTitle = `${frontmatter.title} — Chosen Help`
  const url = `${SITE}${path}`
  const ogImage = resolveOgImage(frontmatter.ogImage)
  const jsonLd = buildJsonLd(frontmatter, url)

  return (
    <Head>
      <title>{fullTitle}</title>
      <meta name="description" content={frontmatter.description} />
      {frontmatter.keywords && frontmatter.keywords.length > 0 && (
        <meta name="keywords" content={frontmatter.keywords.join(', ')} />
      )}
      <link rel="canonical" href={url} />

      <meta property="og:title" content={frontmatter.title} />
      <meta property="og:description" content={frontmatter.description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content="Chosen Help" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={frontmatter.title} />
      <meta name="twitter:description" content={frontmatter.description} />
      <meta name="twitter:image" content={ogImage} />

      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Head>
  )
}

function resolveOgImage(ogImage?: string): string {
  if (!ogImage) return DEFAULT_OG
  if (ogImage.startsWith('http')) return ogImage
  return `${SITE}${ogImage.startsWith('/') ? '' : '/'}${ogImage}`
}

export function buildJsonLd(fm: SeoFrontmatter, url: string): object | null {
  const base = {
    '@context': 'https://schema.org',
    url,
    name: fm.title,
    description: fm.description,
    publisher: { '@type': 'Organization', name: 'Chosen' },
  }
  if (fm.schema === 'Article') {
    return {
      ...base,
      '@type': 'Article',
      headline: fm.title,
      author: { '@type': 'Organization', name: 'Chosen' },
    }
  }
  if (fm.schema === 'HowTo') {
    return { ...base, '@type': 'HowTo' }
  }
  if (fm.schema === 'FAQPage') {
    // Real FAQ structure would parse question h2s + bodies. For now this
    // declares the page type so search engines can surface it; expand when
    // the first true FAQ page lands.
    return { ...base, '@type': 'FAQPage' }
  }
  return null
}
